// Stega encodes Presentation's overlay metadata as invisible characters
// appended to every string it touches. That is harmless for prose, and fatal
// for a string the app uses as code:
//
//   headingLevel "h2" becomes "h2" + ~1250 invisible characters, so
//     React.createElement() throws "Invalid tag: h2..." and the page 500s
//   alignment "left" becomes an unknown key, so styles[alignment] is
//     undefined and the block silently loses its layout class
//   hex "#f36" -> an invalid CSS colour
//
// Encoding buys nothing on these fields either: there is no visible text for an
// overlay to attach to. So they are excluded, and prose keeps its metadata.
//
// Derived from the consumers rather than guessed:
//   headingLevel      CaseStudyTeam.tsx:16, case-studies/[slug].tsx:157 (JSX tag)
//   alignment, layout styles[…] in Body, Fold, Talents, Capabilities, FiftyFifty
//   textAlignment     ColumnsLayout.tsx:65 (textAlign)
//   hex               inline colour/background values
//
// Add a field here when a new schema string starts feeding a tag name, a
// CSS-module class key, or a CSS value.
const NON_DISPLAY_FIELDS = new Set([
  'headingLevel',
  'alignment',
  'textAlignment',
  'layout',
  'layoutSelector',
  'hex',
  'className',
  'ratio',
  'stylesGroup',
  'bgGradient',
  'colorStart',
  'cssUnit',
  'jumpName',
  'namespace',
]);

// True when the value at this document path is consumed as code, not shown as
// text — in which case stega must leave it alone.
//
// Segments are typed loosely on purpose: Sanity's ContentSourceMapParsedPath
// mixes field names, array indices and keyed segments ({_key}). Only a string
// final segment can name a field, and the typeof check narrows to exactly that.
export function isNonDisplayField(sourcePath: readonly unknown[] | undefined): boolean {
  const field = sourcePath?.at(-1);
  return typeof field === 'string' && NON_DISPLAY_FIELDS.has(field);
}
